import { useId, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { cn } from '../lib/cn';
import { Button } from './button';

/** Size of the drawing in pixels: the proportions of the signature line in documents. */
export const SIGNATURE_WIDTH = 900;
export const SIGNATURE_HEIGHT = 300;
const LINE_WIDTH = 3.5;

interface Point {
  x: number;
  y: number;
}

interface Stroke {
  pointerId: number;
  context: CanvasRenderingContext2D;
  points: Point[];
}

function midpoint(a: Point, b: Point): Point {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

export interface SignaturePadProps {
  /**
   * Called after every stroke with the drawing as PNG data URL on a transparent background, and
   * with `null` after clearing.
   */
  onChange: (image: string | null) => void;
  /** Accessible name of the drawing area, e.g. „Unterschrift zeichnen“. */
  label: string;
  /** Text of the button that clears the drawing, e.g. „Neu beginnen“. */
  clearLabel: string;
  /** Hint below the area, e.g. that an uploaded image works without drawing. */
  hint?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * Area to sign on with finger, pen or mouse. Draws in ink on paper in both themes, since the
 * signature lands on white paper, and smooths the line between the pointer positions. Drawing
 * needs a pointer: offer uploading an image next to it as the alternative (WCAG 2.5.7).
 */
export function SignaturePad({
  onChange,
  label,
  clearLabel,
  hint,
  disabled = false,
  className,
}: SignaturePadProps) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const stroke = useRef<Stroke | null>(null);
  const [empty, setEmpty] = useState(true);
  const hintId = useId();

  function pointOf(event: { clientX: number; clientY: number }): Point {
    const rect = canvas.current!.getBoundingClientRect();
    return {
      x: ((event.clientX - rect.left) / (rect.width || SIGNATURE_WIDTH)) * SIGNATURE_WIDTH,
      y: ((event.clientY - rect.top) / (rect.height || SIGNATURE_HEIGHT)) * SIGNATURE_HEIGHT,
    };
  }

  function start(event: ReactPointerEvent<HTMLCanvasElement>) {
    if (disabled || stroke.current || (event.pointerType === 'mouse' && event.button !== 0)) return;
    const element = event.currentTarget;
    const context = element.getContext('2d');
    if (!context) return;
    event.preventDefault();
    element.setPointerCapture(event.pointerId);
    context.lineWidth = LINE_WIDTH;
    context.lineCap = 'round';
    context.lineJoin = 'round';
    const ink = getComputedStyle(element).getPropertyValue('--ink').trim();
    if (ink) {
      context.strokeStyle = ink;
      context.fillStyle = ink;
    }
    const point = pointOf(event);
    stroke.current = { pointerId: event.pointerId, context, points: [point] };
    // A dot, so that a tap leaves a mark too.
    context.beginPath();
    context.arc(point.x, point.y, LINE_WIDTH / 2, 0, Math.PI * 2);
    context.fill();
  }

  function move(event: ReactPointerEvent<HTMLCanvasElement>) {
    const current = stroke.current;
    if (!current || event.pointerId !== current.pointerId) return;
    event.preventDefault();
    // Pens report more positions than frames; the coalesced events keep the line precise.
    const native = event.nativeEvent;
    const coalesced =
      typeof native.getCoalescedEvents === 'function' ? native.getCoalescedEvents() : [];
    const { context, points } = current;
    for (const position of coalesced.length > 0 ? coalesced : [native]) {
      const point = pointOf(position);
      const previous = points[points.length - 1]!;
      if (point.x === previous.x && point.y === previous.y) continue;
      points.push(point);
      // A curve from the middle of the last segment to the middle of the new one, with the
      // previous position as control point: the line bends without corners.
      const from = points.length > 2 ? midpoint(points[points.length - 3]!, previous) : previous;
      const to = midpoint(previous, point);
      context.beginPath();
      context.moveTo(from.x, from.y);
      context.quadraticCurveTo(previous.x, previous.y, to.x, to.y);
      context.stroke();
    }
  }

  function end(event: ReactPointerEvent<HTMLCanvasElement>) {
    const current = stroke.current;
    if (!current || event.pointerId !== current.pointerId) return;
    stroke.current = null;
    const { context, points } = current;
    if (points.length > 1) {
      const last = points[points.length - 1]!;
      const from = midpoint(points[points.length - 2]!, last);
      context.beginPath();
      context.moveTo(from.x, from.y);
      context.lineTo(last.x, last.y);
      context.stroke();
    }
    setEmpty(false);
    onChange(event.currentTarget.toDataURL('image/png'));
  }

  function clear() {
    canvas.current?.getContext('2d')?.clearRect(0, 0, SIGNATURE_WIDTH, SIGNATURE_HEIGHT);
    stroke.current = null;
    setEmpty(true);
    onChange(null);
  }

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div className="relative">
        <canvas
          ref={canvas}
          width={SIGNATURE_WIDTH}
          height={SIGNATURE_HEIGHT}
          role="img"
          aria-label={label}
          aria-describedby={hint ? hintId : undefined}
          className={cn(
            'block aspect-[3/1] w-full touch-none rounded-md border border-dashed border-line-strong bg-paper select-none',
            disabled ? 'cursor-not-allowed opacity-50' : 'cursor-crosshair',
          )}
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerCancel={end}
        />
        {/* The signature line, not part of the drawing. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-[8%] bottom-[24%] border-b border-ink/20"
        />
      </div>
      <div className="flex flex-wrap items-start justify-between gap-2">
        {hint && (
          <p id={hintId} className="min-w-0 flex-1 text-xs text-fg-muted">
            {hint}
          </p>
        )}
        <Button
          variant="ghost"
          size="sm"
          icon="rotate-ccw"
          className="ml-auto"
          disabled={disabled || empty}
          onClick={clear}
        >
          {clearLabel}
        </Button>
      </div>
    </div>
  );
}
